import React, {useCallback, useMemo, useRef} from "react";
import classnames from "classnames";
import update from "immutability-helper";
import {DndProvider, useDrag, useDrop} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {HolderOutlined} from "@ant-design/icons";
import type {ColumnsType, ColumnType} from "antd/lib/table";
import type {TableProps} from "antd";
import {SORTER_SELECTOR, getSortIconTooltipProps, useSortIconOnlyClickGate} from "./SortIconOnlyHeaderCell";
import {TablesEnum} from "../../generated/graphql";
import {usePersistColumns} from "../ColumnConfigurator/persistColumns";
import css from "./useColumnHeaderDnd.less";
// `sortIconOnly` lebt bewusst in SortIconOnlyHeaderCell.less (PR#1) — dort sitzt
// auch die `::after`-Overlay-Neutralisierung, die diese Klasse triggert. Daher
// von DORT beziehen, nicht aus useColumnHeaderDnd.less (sonst undefined).
import sortCss from "./SortIconOnlyHeaderCell.less";

// react-dnd Item-Typ für alle Spalten-Header-Drags dieser App.
const COLUMN_DRAG_TYPE = "ko-list-column-header";

// Das von uns über `onHeaderCell` durchgereichte Drag-Item.
type ColumnDragItem = {columnKey: string};

/**
 * Berechnet die neue Reihenfolge der Spalten-Keys, wenn die Spalte `dragKey`
 * auf die Position von `dropKey` gezogen wird.
 *
 * REINE Funktion (keine Seiteneffekte) — daher direkt unit-testbar. Operiert
 * IMMER auf dem vollständigen Key-State (`columnsToShow`), niemals auf der
 * angezeigten Reihenfolge: Die angezeigten Spalten weichen durch Post-Moves
 * (fixed-left zuerst, fixed-right zuletzt, rowIndex ganz vorn) von der
 * `columnsToShow`-Reihenfolge ab. Ein Reorder per angezeigtem Index würde die
 * falschen Keys verschieben — deshalb rechnen wir konsequent per Key.
 *
 * @param columnsToShow - Vollständige, geordnete Liste der sichtbaren Spalten-Keys.
 * @param dragKey - Key der gezogenen Spalte.
 * @param dropKey - Key der Spalte, auf der fallengelassen wurde.
 * @returns Neue Key-Reihenfolge; bei No-op (Key fehlt oder gleiche Position) das
 *          unveränderte Eingabe-Array (Referenzgleichheit bleibt erhalten).
 */
export const reorderColumnsByKey = (columnsToShow: Array<string>, dragKey: string, dropKey: string): Array<string> => {
    const from = columnsToShow.indexOf(dragKey);
    const to = columnsToShow.indexOf(dropKey);

    // No-op: einer der Keys ist nicht (mehr) sichtbar oder es wird auf sich selbst
    // gedroppt. Unverändertes Array zurückgeben (keine neue Referenz -> kein
    // unnötiges Re-Render / kein Persist beim Aufrufer).
    if (from < 0 || to < 0 || from === to) {
        return columnsToShow;
    }

    // immutability-helper `$splice` führt die Operationen NACHEINANDER auf demselben
    // (intern kopierten) Array aus:
    //   1. [from, 1]        -> entferne das gezogene Element
    //   2. [to, 0, dragKey] -> füge es an Index `to` wieder ein
    // Da `to` aus dem ORIGINAL-Array stammt, `from` aber bereits entfernt wurde,
    // ergibt sich für beide Richtungen die intuitive Reihenfolge:
    //   [A,B,C,D] dragA dropC -> [B,C,A,D]
    //   [A,B,C,D] dragD dropB -> [A,D,B,C]
    return update(columnsToShow, {$splice: [[from, 1], [to, 0, dragKey]]});
};

/** Minimal-Form einer Spalte, aus der wir Key und `fixed` ablesen. */
type ColumnLike = {
    fixed?: ColumnType<unknown>["fixed"];
    dataIndex?: unknown;
    key?: React.Key;
};

/**
 * Liest den Spalten-Key aus einer Spalte (bevorzugt `dataIndex`, sonst `key`).
 * Gibt einen String oder `undefined` zurück (z. B. bei zusammengesetzten/leeren
 * Spalten). Nur String-Keys lassen sich gegen `columnsToShow` matchen.
 */
const getColumnKey = (col: ColumnLike): string | undefined => {
    if (typeof col.dataIndex === "string") {
        return col.dataIndex;
    }
    if (typeof col.key === "string") {
        return col.key;
    }
    return undefined;
};

/**
 * Entscheidet, ob eine Spalte per Drag-and-Drop verschoben werden darf.
 *
 * Nicht verschiebbar sind:
 *  - fixierte Spalten (`fixed: "left"` oder `fixed: "right"`) — ihre Position
 *    wird durch die Post-Moves erzwungen, ein Reorder wäre wirkungslos/irritierend.
 *  - Pflicht-Spalten (`mandatoryColumns`) und gepinnte Spalten (`pinnedColumns`).
 *
 * @param col - Die zu prüfende Spalte (benötigt `fixed` und `dataIndex`/`key`).
 * @param opts - Pflicht-/Pin-Listen von Spalten-Keys.
 */
export const isColumnDraggable = (col: ColumnLike, opts: {mandatoryColumns?: Array<string>; pinnedColumns?: Array<string>}): boolean => {
    if (col.fixed === "left" || col.fixed === "right") {
        return false;
    }

    const key = getColumnKey(col);
    if (key === undefined) {
        // Ohne stabilen String-Key kann nicht per Key reordert werden -> nicht draggable.
        return false;
    }

    const mandatory = opts.mandatoryColumns ?? [];
    const pinned = opts.pinnedColumns ?? [];

    if (mandatory.some((value) => value === key) || pinned.some((value) => value === key)) {
        return false;
    }

    return true;
};

// ---------------------------------------------------------------------------
// Header-Zelle
// ---------------------------------------------------------------------------

// Die Custom-Props, die wir über `onHeaderCell` injizieren. Sie dürfen NICHT auf
// das DOM-`<th>` durchgereicht werden (sonst React-DOM-Warnings), daher tragen
// sie ein eindeutiges Präfix und werden vor dem Spread herausgefiltert.
type InjectedHeaderCellProps = {
    koColumnKey?: string;
    koMoveColumn?: (dragKey: string, dropKey: string) => void;
    koIsDraggable?: boolean;
};

type StandardThProps = React.ThHTMLAttributes<HTMLTableCellElement>;

type DraggableSortHeaderCellProps = StandardThProps & InjectedHeaderCellProps;

/**
 * Header-Zelle, die Sortieren (nur per Caret-Klick) und Drag-and-Drop der Spalte
 * koexistieren lässt.
 *
 * Zusammenspiel der Mechanismen:
 *  - SORTIEREN: `useSortIconOnlyClickGate` verschluckt Klicks außerhalb des Carets
 *    (`SORTER_SELECTOR`); zusammen mit der `css.sortIconOnly`-Klasse (PR#1
 *    neutralisiert das AntD-`::after`-Overlay) ist nur der Caret ein Sort-Target.
 *  - DRAG-QUELLE: ein dediziertes, absolut positioniertes Grip-Overlay (NICHT das
 *    ganze <th>), das nur bei `isDraggable` gerendert wird.
 *  - DROP-ZIEL: das ganze <th>, aber nur wenn `isDraggable` (fixed/pinned/mandatory
 *    sind kein Drop-Ziel).
 *  - CARET-ÜBER-GRIP: der Sorter wird per scoped z-index über das Grip-Overlay
 *    gehoben, damit der Caret klickbar bleibt, obwohl das Overlay den Titelbereich
 *    abdeckt.
 *  - PHANTOM-SORT-GUARD: `didDragRef` wird im Drag-`end`-Callback gesetzt (mit
 *    setTimeout(0)-Reset); ein direkt nach dem Drop ausgelöster th-`onClick` wird
 *    einmalig verschluckt, damit ein Drop nie versehentlich sortiert — ohne den
 *    ersten echten Caret-Klick NACH einem Drag zu blockieren.
 *
 * Nicht-sortierbare UND nicht-draggable Zellen werden als schlichtes <th>
 * gerendert (Verhalten exakt wie SortIconOnlyHeaderCell).
 */
export const DraggableSortHeaderCell: React.FC<DraggableSortHeaderCellProps> = (props) => {
    // Custom-Props herausfiltern, damit sie NICHT als DOM-Attribute landen.
    const {koColumnKey, koMoveColumn, koIsDraggable, onClick, className, ...rest} = props;

    const thRef = useRef<HTMLTableCellElement>(null);
    const gripRef = useRef<HTMLDivElement>(null);

    // Wurde gerade gedraggt? Schützt vor dem Phantom-Sort-Klick direkt nach dem Drop.
    const didDragRef = useRef(false);

    // Stabiler Caret-Gate-Handler (Sort nur bei Caret-Klick), oder undefined wenn
    // die Spalte nicht sortierbar ist. Hook IMMER vor early-returns aufrufen.
    const gatedSortHandler = useSortIconOnlyClickGate(onClick);

    const isDraggable = koIsDraggable === true;
    const columnKey = koColumnKey;

    // DROP-ZIEL: Funktions-Spec-Form mit Deps-Array. So lesen `drop`/`canDrop`
    // IMMER die frischen `columnKey`/`koMoveColumn`/`isDraggable` (kein Stale-
    // Closure-No-op wie im bekannten DnD-Bug dieses Repos).
    const [{isOver, canDrop}, dropConnector] = useDrop<ColumnDragItem, void, {isOver: boolean; canDrop: boolean}>(
        () => ({
            accept: COLUMN_DRAG_TYPE,
            canDrop: (item) => isDraggable && columnKey !== undefined && item.columnKey !== columnKey,
            drop: (item) => {
                if (!isDraggable || columnKey === undefined || typeof koMoveColumn !== "function") {
                    return;
                }
                if (item.columnKey === columnKey) {
                    return;
                }
                koMoveColumn(item.columnKey, columnKey);
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop(),
            }),
        }),
        [columnKey, koMoveColumn, isDraggable],
    );

    // DRAG-QUELLE: ebenfalls Funktions-Spec-Form mit Deps. Im `end`-Callback (nach
    // Drag-ENDE) setzen wir `didDragRef`, damit der unmittelbar folgende th-onClick
    // (Phantom-Sort) verschluckt wird — mit setTimeout(0)-Reset, damit das Flag nicht
    // hängenbleibt.
    const [{isDragging}, dragConnector] = useDrag<ColumnDragItem, void, {isDragging: boolean}>(
        () => ({
            type: COLUMN_DRAG_TYPE,
            item: {columnKey: columnKey as string},
            canDrag: () => isDraggable && columnKey !== undefined,
            // Nach Drag-ENDE (Drop ODER Abbruch) den unmittelbar folgenden, synchronen
            // Phantom-Klick auf das <th> verschlucken — aber NICHT dauerhaft: ohne das
            // setTimeout(0)-Reset würde der erste echte Caret-Klick NACH einem Drag nicht
            // mehr sortieren (das Flag bliebe hängen, falls gar kein Phantom-Klick kommt).
            end: () => {
                didDragRef.current = true;
                setTimeout(() => {
                    didDragRef.current = false;
                }, 0);
            },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [columnKey, isDraggable],
    );

    // Drop immer auf das <th> verbinden; Drag nur auf den Grip.
    dropConnector(thRef);
    dragConnector(gripRef);

    // th-onClick: Phantom-Sort-Guard + Caret-Gate.
    const handleThClick = useCallback<React.MouseEventHandler<HTMLTableCellElement>>(
        (event) => {
            if (didDragRef.current) {
                // Direkt nach einem Drag/Drop: diesen einen Klick verschlucken.
                didDragRef.current = false;
                return;
            }
            if (typeof gatedSortHandler === "function") {
                gatedSortHandler(event);
            }
        },
        [gatedSortHandler],
    );

    const sortable = typeof gatedSortHandler === "function";

    // Komplett passiv (weder sortierbar noch draggable): schlichtes <th>, exakt wie
    // SortIconOnlyHeaderCell für solche Zellen. Kein Ref/Overlay nötig.
    if (!sortable && !isDraggable) {
        return <th className={className} {...rest} />;
    }

    const thClassName = classnames(className, sortCss.sortIconOnly, css.dndHeaderCell, {
        [css.dndDropOver]: isDraggable && isOver && canDrop,
        [css.dndDragging]: isDragging,
    });

    return (
        <th
            ref={thRef}
            className={thClassName}
            {...rest}
            // onClick nur setzen, wenn sortierbar (sonst nichts zu gaten); der
            // Phantom-Guard ist Teil des sortierbaren Pfads.
            onClick={sortable ? handleThClick : undefined}
        >
            {rest.children}
            {isDraggable && (
                <div
                    ref={gripRef}
                    className={css.dndGrip}
                    // Greifbare Fläche über dem Titel. Das 6-Punkt-Griff-Icon
                    // erscheint nur bei Header-Hover (Discoverability ohne Rauschen).
                    aria-hidden="true"
                    title="Spalte verschieben"
                    data-testid="column-drag-grip"
                >
                    <HolderOutlined className={css.dndGripIcon} />
                </div>
            )}
        </th>
    );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type TableComponentsOf<RecordType> = NonNullable<TableProps<RecordType>["components"]>;

export type UseColumnHeaderDndParams<RecordType> = {
    /** Die gerenderten/angezeigten Spalten (mit `fixed` + Post-Moves). */
    columns: ColumnsType<RecordType>;
    /** Vollständige, geordnete Liste der sichtbaren Spalten-Keys. */
    columnsToShow: Array<string>;
    /** State-Setter für `columnsToShow`. */
    setColumnsToShow: (next: Array<string>) => void;
    /** Ziel-Tabelle für die Persistenz. */
    tablesEnum: TablesEnum;
    /** Pflicht-Spalten (nicht verschiebbar/kein Drop-Ziel). */
    mandatoryColumns?: Array<string>;
    /** Gepinnte Spalten (nicht verschiebbar/kein Drop-Ziel). */
    pinnedColumns?: Array<string>;
    /** Evtl. bestehende `components` (z. B. `body.row` der Zahlungsliste). */
    existingComponents?: TableComponentsOf<RecordType>;
};

export type UseColumnHeaderDndResult<RecordType> = {
    /** `components` mit DraggableSortHeaderCell als header.cell (body.row bleibt erhalten). */
    components: TableComponentsOf<RecordType>;
    /** Spalten mit injiziertem `onHeaderCell` (Custom-Props + erhaltenes AntD-onClick). */
    dndColumns: ColumnsType<RecordType>;
    /**
     * Hüllt einen Knoten in einen Table-lokalen DndProvider. Seit dem Root-Provider
     * in app.tsx teilen alle DndProvider dieselbe react-dnd-Singleton-Instanz
     * (refCount dort dauerhaft ≥ 1, kein Teardown-Race bei Routenwechseln mehr) —
     * der lokale Wrapper ist ein No-op-Reuse und bleibt nur, damit die Tabellen
     * auch isoliert (Tests) ohne Root-Provider funktionieren.
     */
    wrapWithDnd: (node: React.ReactNode) => React.ReactElement;
};

/**
 * Generischer Kern für Spalten-Header-Drag-and-Drop in Admin-Listen-Tabellen.
 *
 * Liefert `components` (header.cell = DraggableSortHeaderCell), `dndColumns`
 * (Spalten mit `onHeaderCell`, das Key/moveColumn/isDraggable injiziert und ein
 * evtl. bestehendes `onHeaderCell`/AntD-Sort-onClick zusammenführt) und
 * `wrapWithDnd` (Table-lokaler DndProvider).
 *
 * Persistenz: Pro Drop wird `setColumnsToShow` UND `persistColumns` GENAU EINMAL
 * aufgerufen — niemals im Hover. Bei einem No-op-Reorder (gleiche Key-Reihenfolge)
 * passiert nichts.
 */
export const useColumnHeaderDnd = <RecordType,>(params: UseColumnHeaderDndParams<RecordType>): UseColumnHeaderDndResult<RecordType> => {
    const {columns, columnsToShow, setColumnsToShow, tablesEnum, mandatoryColumns, pinnedColumns, existingComponents} = params;

    const persistColumns = usePersistColumns();

    // Eine Reorder-Operation: neue Key-Reihenfolge berechnen, dann State + Persist
    // GENAU EINMAL. Bei No-op (Referenzgleichheit) nichts tun.
    const moveColumn = useCallback(
        (dragKey: string, dropKey: string) => {
            const next = reorderColumnsByKey(columnsToShow, dragKey, dropKey);

            // reorderColumnsByKey gibt bei No-op das identische Array zurück.
            if (next === columnsToShow) {
                return;
            }

            setColumnsToShow(next);
            persistColumns(tablesEnum, next);
        },
        [columnsToShow, setColumnsToShow, tablesEnum, persistColumns],
    );

    // components: header.cell durch DraggableSortHeaderCell ersetzen, restliche
    // Overrides (body.row, header.wrapper, ...) erhalten.
    const components = useMemo<TableComponentsOf<RecordType>>(
        () => ({
            ...existingComponents,
            header: {
                ...(existingComponents && existingComponents.header),
                cell: DraggableSortHeaderCell,
            },
        }),
        [existingComponents],
    );

    // dndColumns: jede Spalte bekommt ein onHeaderCell, das die Custom-Props
    // injiziert und ein evtl. bestehendes onHeaderCell (AntD-Sort-onClick!)
    // zusammenführt.
    const dndColumns = useMemo<ColumnsType<RecordType>>(
        () =>
            columns.map((col) => {
                const columnLike = col as ColumnLike;
                const key = getColumnKey(columnLike);
                const draggable = isColumnDraggable(columnLike, {mandatoryColumns, pinnedColumns});

                const existingOnHeaderCell = (col as ColumnType<RecordType>).onHeaderCell;

                const onHeaderCell: ColumnType<RecordType>["onHeaderCell"] = (column) => {
                    // Zuerst die bestehenden Header-Cell-Props (enthalten u. a. das
                    // AntD-Sort-onClick) holen, damit sie NICHT verloren gehen.
                    const base = (existingOnHeaderCell ? existingOnHeaderCell(column) : {}) as React.HTMLAttributes<HTMLTableCellElement> & InjectedHeaderCellProps;

                    return {
                        ...base,
                        koColumnKey: key,
                        koMoveColumn: moveColumn,
                        koIsDraggable: draggable,
                    } as React.HTMLAttributes<HTMLTableCellElement>;
                };

                return {...col, onHeaderCell} as ColumnType<RecordType>;
            }),
        [columns, mandatoryColumns, pinnedColumns, moveColumn],
    );

    // wrapWithDnd: DndProvider pro Table — reuset die globale react-dnd-Singleton-
    // Instanz, die der Root-Provider in app.tsx dauerhaft am Leben hält.
    const wrapWithDnd = useCallback(
        (node: React.ReactNode): React.ReactElement => <DndProvider backend={HTML5Backend}>{node}</DndProvider>,
        [],
    );

    return {components, dndColumns, wrapWithDnd};
};

// Re-Export, damit Konsumenten Caret-Selektor UND Sort-Tooltip-Props aus einem
// Modul beziehen können (eine Import-Quelle für die Listen-Verdrahtung).
export {SORTER_SELECTOR, getSortIconTooltipProps};
